import type { BudgetOpexWorkspace, SafetyProgramWorkspace } from '@kop/contracts';
import type { EducationRecord } from '@kop/safety-education';
import type { VictimRegistryListItem } from '@kop/incidents-emergency';
import type { MeetingMinutesPage } from '../communications/meeting-minutes-model';
import type { OperationsSummary } from './operations-summary-port';
import type { SiteInspectionSummary } from './site-inspection-summary-port';
import { createSessionKeyedReadCache } from '../../runtime/session-read-cache';
import { meetingMonthRange } from './monthly-meeting-read';
import { budgetReportingSummary } from '../budget-materials/budget-reporting';

export const monthlyAgenda = [
  ['plan', '안전보건 계획사항 이행내역'],
  ['risk', '유해·위험요인 확인 및 개선 현황'],
  ['budget', '안전보건 예산 집행 내역'],
  ['voice', '종사자 의견청취'],
  ['accident', '안전사고 현황 및 조치 내역'],
  ['education', '교육현황'],
] as const;
export type MonthlyAgendaId = (typeof monthlyAgenda)[number][0];
export type AgendaRow = {
  key: string;
  label: string;
  cells: (number | string | null)[];
  featureId: string;
  note?: string;
};
export type AgendaReport = {
  headings: string[];
  rows: AgendaRow[];
  notes: string[];
  sumColumns: number[];
  at: number;
  retained?: boolean;
};
export type AgendaInput = {
  uid: string;
  siteId: string;
  month: string;
  meetingMonth: string;
  section: MonthlyAgendaId;
};
export type AgendaPorts = {
  plan(siteId: string, year: number): Promise<SafetyProgramWorkspace>;
  operations(siteId: string, month: string, fresh: boolean): Promise<OperationsSummary>;
  inspection(
    uid: string,
    siteId: string,
    month: string,
    fresh: boolean,
  ): Promise<SiteInspectionSummary>;
  budget(siteId: string, from: string, to: string): Promise<BudgetOpexWorkspace>;
  meetings(siteId: string, month: string): Promise<MeetingMinutesPage>;
  accidents(siteId: string, year: number): Promise<readonly VictimRegistryListItem[]>;
  education(siteId: string): Promise<readonly EducationRecord[]>;
};
const ratio = (actual: number | null, target: number | null) =>
  actual === null || target === null || target <= 0
    ? null
    : `${Math.round((actual / target) * 1000) / 10}%`;
const kstDate = (value: string) => {
  if (!Number.isFinite(Date.parse(value))) throw Error('INVALID_REPORT_DATE');
  return new Date(value).toLocaleDateString('sv-SE', { timeZone: 'Asia/Seoul' });
};
const annualPlan = (value: BudgetOpexWorkspace, year: number) => {
  const plan = value.plans
    .filter((row) => row.year === year)
    .sort((a, b) => b.updatedAt.localeCompare(a.updatedAt) || a.id.localeCompare(b.id))[0];
  return plan ? (plan.annualAmount ?? plan.rows.reduce((sum, row) => sum + row.amount, 0)) : null;
};
const code = (error: unknown) =>
  error && typeof error === 'object' && 'code' in error ? String(error.code) : '';

export function createMonthlyAgendaReader(ports: AgendaPorts) {
  const cache = createSessionKeyedReadCache<AgendaReport>(48);
  const key = (input: AgendaInput) =>
    JSON.stringify([input.uid, input.siteId, input.month, input.meetingMonth, input.section]);
  async function calculate(input: AgendaInput, fresh: boolean): Promise<AgendaReport> {
    const { siteId, month, meetingMonth, section, uid } = input;
    const year = Number(month.slice(0, 4)),
      index = Number(month.slice(5)) - 1;
    const report = (
      headings: string[],
      rows: AgendaRow[],
      notes: string[] = [],
      sumColumns: number[] = [],
    ): AgendaReport => ({ headings, rows, notes, sumColumns, at: Date.now() });
    if (section === 'plan') {
      const data = await ports.plan(siteId, year);
      if (data.siteId !== siteId || data.year !== year || !data.complete)
        throw Error('PLAN_REPORT_INCOMPLETE');
      const rows = data.plans
        .filter((plan) => plan.active)
        .map((plan) => {
          const result = data.planResults.find((row) => row.planId === plan.id);
          const actual =
            plan.source === 'manual'
              ? (plan.manualActuals?.[index] ?? null)
              : result?.complete
                ? (result.monthlyActuals[index] ?? null)
                : null;
          const target = plan.monthlyTargets[index] ?? null;
          return {
            key: plan.id,
            label: plan.title,
            cells: [plan.category, target, actual, ratio(actual, target)],
            featureId: 'activity-plan',
          };
        });
      return report(['추진과제', '구분', '월 계획', '월 실적', '달성률'], rows, [
        '활동계획에 저장된 실적을 사용합니다. 증빙 파일 수를 실적 횟수에 더하지 않습니다.',
      ]);
    }
    if (section === 'risk') {
      const [inspection, operations] = await Promise.all([
        ports.inspection(uid, siteId, month, fresh),
        ports.operations(siteId, month, fresh),
      ]);
      return report(
        ['구분', '평가·지적', '조치 완료', '미조치', '조치율'],
        [
          {
            key: 'inspection',
            label: '점검 지적사항',
            cells: [
              inspection.findings,
              inspection.completed,
              inspection.unresolved,
              ratio(inspection.completed, inspection.findings),
            ],
            featureId: 'inspection-reporting',
            note: `점검 실시 ${inspection.inspections}회`,
          },
          {
            key: 'permit',
            label: '수시 위험성평가 (제출 허가서)',
            cells: [operations.work?.monthly ?? null, null, null, null],
            featureId: 'work-permit',
            note: '승인 대기 포함, 작성 중·반려·무효 제외',
          },
          {
            key: 'regular',
            label: '정기 위험성평가 (해당 반기 평가서)',
            cells: [operations.risk?.total ?? null, null, null, null],
            featureId: 'regular-risk',
            note: `${year}년 ${index < 6 ? '상반기' : '하반기'} 평가서 기준`,
          },
        ],
        [
          '지적사항과 평가서·허가서 건수는 서로 더하지 않습니다. 허가서 내부 점검·조치와 정기평가 항목별 결과는 원자료에서 확인합니다.',
        ],
        [0, 1, 2],
      );
    }
    if (section === 'budget') {
      const [monthly, cumulative] = await Promise.all([
        ports.budget(siteId, month, month),
        ports.budget(siteId, `${year}-01`, month),
      ]);
      if ([monthly, cumulative].some((page) => !page.complete || page.heldCount !== 0))
        throw Error('BUDGET_REPORT_INCOMPLETE');
      const a = budgetReportingSummary(monthly.aggregates),
        b = budgetReportingSummary(cumulative.aggregates),
        plan = annualPlan(cumulative, year);
      const rows: AgendaRow[] = a.rows.map((row, i) => ({
        key: row.code,
        label: `${row.code} ${row.name}`,
        cells: [row.amount, b.rows[i]!.amount, null, null],
        featureId: 'safety-cost',
      }));
      rows.push({
        key: 'unclassified',
        label: '분류 전',
        cells: [a.unclassified, b.unclassified, null, null],
        featureId: 'safety-cost',
      });
      rows.push({
        key: 'total',
        label: '집행 합계',
        cells: [
          monthly.aggregates.principal,
          cumulative.aggregates.principal,
          plan,
          ratio(cumulative.aggregates.principal, plan),
        ],
        featureId: 'safety-cost',
      });
      return report(
        ['사용 분류', '당월 집행액 (원)', '누계 집행액 (원)', '연간 편성예산 (원)', '집행률'],
        rows,
        [
          'KOP 자체 안전관리 비용 기준입니다. 가장 최근 저장한 연간 예산을 사용하며 예산 수정본을 서로 합산하지 않습니다. 협력업체 사용내역은 비용 집행 관리 상세에서 별도로 확인할 수 있습니다.',
        ],
        [0, 1, 2],
      );
    }
    if (section === 'voice') {
      const page = await ports.meetings(siteId, month);
      const rows: AgendaRow[] = page.records.map((row) => ({
        key: row.id,
        label: `${row.date} ${row.title}`,
        cells: [
          row.discussion || '등록된 논의 없음',
          row.decisions || '등록된 결정사항 없음',
          row.followUps.filter((item) => item.status === 'done').length,
          row.followUps.length,
        ],
        featureId: 'meeting-minutes',
      }));
      return report(
        ['청취 자료', '논의·의견 내용', '개선방안·결정사항', '후속조치 완료', '후속조치 등록'],
        rows,
        [
          '안전보건협의체 회의록과 후속조치를 연결합니다. 문단이나 회의 횟수를 의견 청취 건수로 환산하지 않습니다. 위험성평가 근로자 의견은 평가별 원자료에서 확인합니다.',
        ],
      );
    }
    if (section === 'accident') {
      const accidentYear = Number(meetingMonth.slice(0, 4)),
        to = meetingMonthRange(meetingMonth).to;
      const data = (await ports.accidents(siteId, accidentYear)).filter((row) => {
        const date = kstDate(row.occurredAt);
        return date.startsWith(`${accidentYear}-`) && date <= to;
      });
      const categories = [
        ['industrial', '산업재해'],
        ['minor', '경미상'],
        ['property', '물적피해'],
        ['unclassified', '분류 확인 필요'],
      ] as const;
      const rows: AgendaRow[] = categories.map(([category, label]) => {
        const items = data.filter((row) => (row.accidentCategory ?? 'unclassified') === category);
        return {
          key: category,
          label,
          cells: [
            items.filter((row) => kstDate(row.occurredAt).startsWith(meetingMonth)).length,
            items.length,
            items.filter((row) => row.status === 'closed').length,
            items.filter((row) => row.workStatus === 'under-treatment').length,
          ],
          featureId: 'incident',
        };
      });
      return report(
        ['사고 구분', '회의월 발생', '연간 누계', '누계 종결', '누계 치료 중'],
        rows,
        [
          '사고대장의 발생일과 현재 추적 상태를 사용합니다. 사고 수와 치료·종결 상태는 별도 집계이며, 물적피해 금액 기준은 원자료에서 확인합니다.',
        ],
        [0, 1, 2, 3],
      );
    }
    const records = await ports.education(siteId),
      groups = new Map<string, AgendaRow>();
    for (const row of records) {
      const complete =
        ['recorded', 'approved', 'closed'].includes(row.stage) ||
        (row.recordOrigin.kind === 'legacy-import' &&
          Boolean(row.historicalLifecycle) &&
          Boolean(row.execution?.actualSessions.length));
      if (
        !complete ||
        !row.execution?.actualSessions.some((session) =>
          kstDate(session.startsAt).startsWith(month),
        )
      )
        continue;
      const type = row.standard.types.find((item) => item.typeCode === row.educationTypeCode);
      if (type?.category !== 'special' || row.scope.kind !== 'contractor') continue;
      const organizations = new Map<string, { name: string; count: number }>();
      for (const person of row.targetParticipants)
        organizations.set(person.organizationId, { name: person.organizationName, count: 0 });
      for (const attendance of row.execution.attendance.filter((person) =>
        ['attended', 'partial'].includes(person.status),
      )) {
        const person = row.targetParticipants.find(
          (person) => person.participantId === attendance.participant.participantId,
        );
        if (!person) throw Error('EDUCATION_PARTICIPANT_UNBOUND');
        const organization = organizations.get(person.organizationId) ?? {
          name: person.organizationName,
          count: 0,
        };
        organization.count++;
        organizations.set(person.organizationId, organization);
      }
      for (const [organizationId, organization] of organizations) {
        const key = JSON.stringify([organizationId, row.educationTypeCode, row.title]),
          group = groups.get(key) ?? {
            key,
            label: organization.name,
            cells: [row.title, 0, 0],
            featureId: 'safety-training',
          };
        group.cells[1] = Number(group.cells[1]) + 1;
        group.cells[2] = Number(group.cells[2]) + organization.count;
        groups.set(key, group);
      }
    }
    return report(
      ['협력업체', '특별교육 항목', '교육 참여 건수', '참석 연인원'],
      [...groups.values()],
      [
        '협력업체 특별교육 일지의 실제 교육일과 참석 기록을 집계합니다. 같은 사람이 여러 교육에 참석하면 참석 횟수만큼 합산합니다. 법정교육 이수율은 포함하지 않습니다.',
      ],
      [1, 2],
    );
  }
  return {
    peek: (input: AgendaInput) => cache.peek(key(input)),
    async load(input: AgendaInput, force = false) {
      if (!input.uid || !input.siteId) throw Error('REPORT_SCOPE_REQUIRED');
      meetingMonthRange(input.month);
      meetingMonthRange(input.meetingMonth);
      const saved = cache.peek(key(input));
      try {
        return await cache.read(
          key(input),
          () => calculate(input, force),
          force || !saved || Date.now() - saved.at > 60_000,
        );
      } catch (error) {
        if (
          saved &&
          [
            'functions/unavailable',
            'functions/internal',
            'functions/resource-exhausted',
            'functions/deadline-exceeded',
          ].includes(code(error))
        )
          return { ...saved, retained: true };
        cache.invalidate(key(input));
        throw error;
      }
    },
  };
}

const defaultPorts: AgendaPorts = {
  plan: async (siteId, year) =>
    (await import('../safety-program/repository')).createSafetyProgramRepository(siteId).load(year),
  operations: async (siteId, month, fresh) =>
    (await import('./operations-summary-port')).loadOperationsSummary({
      siteId,
      month,
      day: new Date().toLocaleDateString('sv-SE', { timeZone: 'Asia/Seoul' }),
      features: ['regular-risk', 'work-permit'],
      fresh,
    }),
  inspection: async (uid, siteId, month, fresh) =>
    (await import('./site-inspection-summary-port')).loadSiteInspectionSummary(
      uid,
      siteId,
      meetingMonthRange(month),
      fresh,
    ),
  budget: async (siteId, from, to) => {
    const port = (
      await import('../budget-materials/budget-opex-repository')
    ).createBudgetOpexRepository(null, siteId);
    if (!port) throw Error('BUDGET_UNAVAILABLE');
    return port.load({
      from,
      to,
      owner: 'principal',
      company: '',
      category: '',
      search: '',
      offset: 0,
    });
  },
  meetings: async (siteId, month) => {
    const port = (
        await import('../communications/meeting-minutes-repository')
      ).createMeetingMinutesRepository(siteId),
      period = meetingMonthRange(month),
      records = new Map<string, MeetingMinutesPage['records'][number]>(),
      seen = new Set<string>();
    let cursor: string | null = null,
      first: MeetingMinutesPage | null = null;
    for (let page = 0; page < 40; page++) {
      const value = await port.list({
        search: '',
        dateFrom: period.from,
        dateTo: period.to,
        kind: 'committee',
        cadence: 'monthly',
        cursor,
      });
      first ??= value;
      for (const row of value.records) {
        if (records.has(row.id)) throw Error('MEETING_REPORT_CHANGED');
        records.set(row.id, row);
      }
      cursor = value.nextCursor;
      if (!cursor) {
        if (records.size !== first.totalCount) throw Error('MEETING_REPORT_INCOMPLETE');
        return { ...first, records: [...records.values()], nextCursor: null };
      }
      if (seen.has(cursor)) throw Error('MEETING_CURSOR_REPEATED');
      seen.add(cursor);
    }
    throw Error('MEETING_REPORT_LIMIT');
  },
  accidents: async (siteId, year) => {
    const port = (
      await import('../incidents-emergency/victim-registry-data-adapter')
    ).createVictimRegistryPort(import.meta.env, siteId);
    let cursor: Parameters<typeof port.list>[0]['cursor'] = null;
    const rows = new Map<string, VictimRegistryListItem>(),
      seen = new Set<string>();
    for (let page = 0; page < 40; page++) {
      const value = await port.list({
        year,
        contractorId: null,
        pageSize: 50,
        cursor,
        includeSummary: false,
        orderBy: 'occurredAt',
      });
      if (value.source !== 'server' || (value.heldCount ?? 0) > 0)
        throw Error('ACCIDENT_REPORT_INCOMPLETE');
      for (const row of value.items) {
        if (rows.has(row.entryId)) throw Error('ACCIDENT_REPORT_CHANGED');
        rows.set(row.entryId, row);
      }
      cursor = value.nextCursor;
      if (!cursor) return [...rows.values()];
      const key = JSON.stringify(cursor);
      if (seen.has(key)) throw Error('ACCIDENT_CURSOR_REPEATED');
      seen.add(key);
    }
    throw Error('ACCIDENT_REPORT_LIMIT');
  },
  education: async (siteId) => {
    const port = await (
      await import('../safety-education/repository')
    ).createSafetyEducationWorkspaceRepository({ kind: 'site', tenantId: 'kop', siteId });
    let cursor: { updatedAt: string; educationId: string } | null = null;
    const rows = new Map<string, EducationRecord>(),
      seen = new Set<string>();
    let expected: number | null = null;
    for (let page = 0; page < 40; page++) {
      const value = await port.listPage({ pageSize: 50, cursor });
      expected ??= value.totalCount;
      for (const row of value.records) {
        if (rows.has(row.educationId)) throw Error('EDUCATION_REPORT_CHANGED');
        rows.set(row.educationId, row);
      }
      cursor = value.nextCursor;
      if (!cursor) {
        if (rows.size !== expected) throw Error('EDUCATION_REPORT_INCOMPLETE');
        return [...rows.values()];
      }
      const key = JSON.stringify(cursor);
      if (seen.has(key)) throw Error('EDUCATION_CURSOR_REPEATED');
      seen.add(key);
    }
    throw Error('EDUCATION_REPORT_LIMIT');
  },
};
export const monthlyAgendaReader = createMonthlyAgendaReader(defaultPorts);
